import React, { Component } from 'react'
import { Alert, Button, Card, Col, Input, Row, Spin, Tag, message } from 'antd'
import {
    ApiOutlined,
    AppleOutlined,
    CloudDownloadOutlined,
    CopyOutlined,
    RocketOutlined,
    SafetyCertificateOutlined,
    SendOutlined,
    WindowsOutlined,
} from '@ant-design/icons'
import { getA2aPortalOverview } from '../../request'

const DOWNLOADS = [
    {
        key: 'macos-arm64',
        icon: <AppleOutlined />,
        platform: 'macOS · Apple 芯片',
        detail: '适用于 M1、M2、M3、M4 等 Apple Silicon Mac',
    },
    {
        key: 'macos-x64',
        icon: <AppleOutlined />,
        platform: 'macOS · Intel 芯片',
        detail: '适用于使用 Intel 处理器的 Mac',
    },
    {
        key: 'windows-x64',
        icon: <WindowsOutlined />,
        platform: 'Windows · 64 位',
        detail: '适用于公司常用的 Windows 10 / 11 电脑',
    },
]

class A2ATab extends Component {
    state = {
        loading: true,
        portalOverview: null,
        portalLoadFailed: false,
    }

    componentDidMount() {
        this.loadPortalOverview()
    }

    loadPortalOverview = async () => {
        this.setState({ loading: true, portalLoadFailed: false })
        try {
            const portalOverview = await getA2aPortalOverview()
            this.setState({ loading: false, portalOverview: portalOverview || {} })
        } catch (error) {
            // 2026-08-08 coder(lq): 发布中心不可用时明确显示失败，不回退到过期或虚构下载地址。
            this.setState({ loading: false, portalOverview: null, portalLoadFailed: true })
        }
    }

    copyPrompt = () => {
        const prompt = this.state.portalOverview?.prompt?.promptContent || ''
        if (!prompt) {
            message.warning('管理员尚未发布任务发送方 Prompt')
            return
        }
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(prompt)
                .then(() => message.success('任务发送方 Prompt 已复制'))
                .catch(() => message.warning('复制失败，请手动选择并复制'))
            return
        }
        message.warning('当前浏览器不支持自动复制，请手动选择并复制')
    }

    downloadAsset = asset => {
        if (!asset?.url) {
            message.warning('该平台安装包尚未发布')
            return
        }
        window.open(asset.url, '_blank')
    }

    render() {
        const { loading, portalOverview, portalLoadFailed } = this.state
        const senderPrompt = portalOverview?.prompt?.promptContent || ''
        const release = portalOverview?.release || null
        const assets = release?.assets || []

        return (
            <div className="profile-a2a-wrap">
                <section className="profile-a2a-hero">
                    <div className="profile-a2a-hero-icon"><SendOutlined /></div>
                    <div>
                        <Tag color="blue">Agent to Agent</Tag>
                        <h1>让任务从一个人的 Codex，安全地交给另一个人的 Codex</h1>
                        <p>
                            A2A 可以把任务标题、内容、项目和执行人一起派发。接收方使用 CodexPlus 收到任务后，
                            可先人工核查，再在自己的 Codex 中创建会话并开始处理；完成后还能把结果状态回传给发送方。
                        </p>
                    </div>
                </section>

                <Row gutter={[16, 16]} className="profile-a2a-flow">
                    <Col xs={24} md={8}>
                        <Card bordered={false} className="profile-a2a-feature-card">
                            <div className="profile-a2a-feature-icon"><SendOutlined /></div>
                            <div className="profile-a2a-feature-content">
                                <strong>1. 发送任务</strong>
                                <p>发送方在 Codex 中整理任务，通过公司 MCP 选择接收人并派发。</p>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card bordered={false} className="profile-a2a-feature-card">
                            <div className="profile-a2a-feature-icon"><RocketOutlined /></div>
                            <div className="profile-a2a-feature-content">
                                <strong>2. CodexPlus 接收</strong>
                                <p>接收方看板实时收到任务，确认项目后创建为 Codex 会话。</p>
                            </div>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card bordered={false} className="profile-a2a-feature-card">
                            <div className="profile-a2a-feature-icon"><SafetyCertificateOutlined /></div>
                            <div className="profile-a2a-feature-content">
                                <strong>3. 确认并回传</strong>
                                <p>用户核查结果并确认完成，任务状态同步回服务端，整个过程可追踪。</p>
                            </div>
                        </Card>
                    </Col>
                </Row>

                <Card bordered={false} className="profile-a2a-section-card">
                    <div className="profile-a2a-section-head">
                        <div>
                            <h2><ApiOutlined /> 配置任务发送方</h2>
                            <p>把下面整段 Prompt 复制给 Codex，它会引导你配置环境变量、安装 MCP，并在发送前要求人工确认。</p>
                        </div>
                        <div className="profile-a2a-section-actions">
                            <Button icon={<CopyOutlined />} type="primary" disabled={!senderPrompt} onClick={this.copyPrompt}>复制 Prompt</Button>
                            {portalLoadFailed && <Button onClick={this.loadPortalOverview}>重试</Button>}
                        </div>
                    </div>

                    <Spin spinning={loading} tip="正在加载任务发送方 Prompt">
                        <Input.TextArea
                            className="profile-a2a-prompt"
                            readOnly
                            value={senderPrompt}
                            autoSize={{ minRows: 10, maxRows: 18 }}
                            placeholder={portalLoadFailed
                                ? '任务发送方 Prompt 加载失败，请点击“重试”重新加载。'
                                : '管理员尚未发布任务发送方 Prompt。'}
                        />
                    </Spin>
                </Card>

                <Card bordered={false} className="profile-a2a-section-card">
                    <div className="profile-a2a-section-head">
                        <div>
                            <h2><RocketOutlined /> CodexPlus 是什么</h2>
                            <p>CodexPlus 是公司内部的 Codex 桌面增强工具。它不替代 Codex，而是把 A2A 和常用效率能力直接放进 Codex 页面。</p>
                        </div>
                    </div>
                    <div className="profile-a2a-capabilities">
                        <span>实时接收 A2A 任务</span>
                        <span>创建原生 Codex 会话</span>
                        <span>项目映射与任务看板</span>
                        <span>会话管理与备份</span>
                        <span>自定义快捷按钮</span>
                        <span>macOS / Windows 支持</span>
                    </div>
                    <Alert
                        showIcon
                        type="info"
                        message="首次使用提示"
                        description="安装后请完全退出官方 Codex，再启动 CodexPlus。CodexPlus 会启动 Codex 并把增强功能加载到页面中；任务不会自动执行，除非你自己在设置里开启了“收到任务后直接执行”。"
                    />
                </Card>

                <Card bordered={false} className="profile-a2a-section-card">
                    <div className="profile-a2a-section-head">
                        <div>
                            <h2><CloudDownloadOutlined /> 下载 CodexPlus</h2>
                            <p>{release ? `当前稳定版 ${release.version}，请选择自己电脑对应的安装包。` : '管理员尚未发布 CodexPlus 稳定版。'}</p>
                        </div>
                        <Button icon={<CloudDownloadOutlined />} onClick={this.loadPortalOverview}>刷新版本</Button>
                    </div>
                    {portalLoadFailed && <Alert showIcon type="error" message="CodexPlus 版本信息加载失败" description="请检查网络后重试。" />}
                    {!portalLoadFailed && !release && <Alert showIcon type="info" message="暂无可下载版本" description="请联系管理员在 A2A 管理页发布版本。" />}
                    {release && <Row gutter={[16, 16]}>
                        {DOWNLOADS.map(download => {
                            const asset = assets.find(item => item.platform === download.key)
                            return (
                            <Col xs={24} lg={8} key={download.key}>
                                <div className="profile-a2a-download-card">
                                    <div className="profile-a2a-download-icon">{download.icon}</div>
                                    <div className="profile-a2a-download-main">
                                        <strong>{download.platform}</strong>
                                        <p>{download.detail}</p>
                                        <small>{asset ? `${asset.name} · SHA-256 ${asset.sha256 || '-'}` : '该平台暂未发布安装包'}</small>
                                    </div>
                                    <Button type="primary" disabled={!asset} icon={<CloudDownloadOutlined />} onClick={() => this.downloadAsset(asset)}>
                                        {asset ? '下载安装包' : '暂不可用'}
                                    </Button>
                                </div>
                            </Col>
                            )
                        })}
                    </Row>}
                </Card>
            </div>
        )
    }
}

export default A2ATab
